/**
 * Seletor de Visibilidade para Regras de Roteamento Manual
 * 
 * Permite definir quem pode ver e executar uma ação rápida:
 * - Privada: apenas o criador
 * - Compartilhada: criador + usuários selecionados
 * - Todos: todos os membros do tenant
 * 
 * @version 1.0.0
 * @date 2026-02-13
 */

import { Lock, Users, Globe } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useTenantMembers } from '@/hooks/useActivityComments';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

// Tipos de visibilidade disponíveis
export type RuleVisibility = 'private' | 'shared' | 'public';

interface RuleVisibilitySelectorProps {
  /** Visibilidade atual */
  visibility: RuleVisibility;
  /** Callback ao mudar visibilidade */
  onVisibilityChange: (v: RuleVisibility) => void;
  /** IDs dos usuários selecionados (quando shared) */
  selectedUserIds: string[];
  /** Callback ao mudar usuários selecionados */
  onSelectedUserIdsChange: (ids: string[]) => void;
}

/**
 * Componente de seleção de visibilidade com lista de membros do tenant
 */
export function RuleVisibilitySelector({
  visibility,
  onVisibilityChange,
  selectedUserIds,
  onSelectedUserIdsChange,
}: RuleVisibilitySelectorProps) {
  const { user } = useAuth();
  // Reutiliza hook existente para buscar membros do tenant
  const { data: members = [], isLoading } = useTenantMembers();

  // Filtra o próprio usuário da lista (criador sempre tem acesso)
  const otherMembers = members.filter((m) => m.id !== user?.id);

  // Toggle de seleção de um membro
  const toggleUser = (userId: string) => {
    if (selectedUserIds.includes(userId)) {
      onSelectedUserIdsChange(selectedUserIds.filter((id) => id !== userId));
    } else {
      onSelectedUserIdsChange([...selectedUserIds, userId]);
    }
  };




  return (
    <div className="space-y-4 p-4 border rounded-lg">
      <h3 className="font-medium">Visibilidade</h3>
      <p className="text-sm text-muted-foreground">
        Defina quem poderá ver e executar esta ação rápida.
      </p>

      {/* Opções de visibilidade */}
      <RadioGroup
        value={visibility}
        onValueChange={(v) => onVisibilityChange(v as RuleVisibility)}
        className="space-y-3"
      >
        {/* Privada */}
        <div className="flex items-center space-x-3 p-3 rounded-lg border bg-muted/20 hover:bg-muted/40 transition-colors">
          <RadioGroupItem value="private" id="vis-private" />
          <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
          <Label htmlFor="vis-private" className="flex-1 cursor-pointer">
            <span className="font-medium">Privada</span>
            <p className="text-xs text-muted-foreground">Apenas você pode ver e executar</p>
          </Label>
        </div>

        {/* Compartilhada */}
        <div className={cn(
          "flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/40 transition-colors",
          visibility === 'shared' ? "bg-primary/5 border-primary/30" : "bg-muted/20"
        )}>
          <RadioGroupItem value="shared" id="vis-shared" />
          <Users className="h-4 w-4 text-muted-foreground shrink-0" />
          <Label htmlFor="vis-shared" className="flex-1 cursor-pointer">
            <span className="font-medium">Compartilhada</span>
            <p className="text-xs text-muted-foreground">Você + usuários selecionados</p>
          </Label>
        </div>

        {/* Todos */}
        <div className="flex items-center space-x-3 p-3 rounded-lg border bg-muted/20 hover:bg-muted/40 transition-colors">
          <RadioGroupItem value="public" id="vis-public" />
          <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
          <Label htmlFor="vis-public" className="flex-1 cursor-pointer">
            <span className="font-medium">Todos</span>
            <p className="text-xs text-muted-foreground">Todos os membros do tenant podem ver e executar</p>
          </Label>
        </div>
      </RadioGroup>

      {/* Lista de membros (apenas quando compartilhada) */}
      {visibility === 'shared' && (
        <div className="mt-4 space-y-2">
          <Label className="text-sm">Selecione os usuários:</Label>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : otherMembers.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3 text-center">
              Nenhum outro membro encontrado no tenant.
            </p>
          ) : (
            <ScrollArea className="h-56 pr-2">
              <div className="space-y-1">
                {otherMembers.map((member) => (
                  <div
                    key={member.id}
                    className={cn(
                      "flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors",
                      selectedUserIds.includes(member.id) && "bg-primary/10"
                    )}
                    onClick={() => toggleUser(member.id)}
                  >
                    <Checkbox
                      checked={selectedUserIds.includes(member.id)}
                      onCheckedChange={() => toggleUser(member.id)}
                    />
                    {/* SmartAvatar resolve storage path + iniciais coloridas + avatar 3D */}
                    <SmartAvatar
                      src={member.avatar_url}
                      name={member.full_name}
                      size="sm"
                      use3DFallback
                    />
                    {/* Nome + email (padrão premium de listagem de usuários) */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {member.full_name || (member as any).email || member.id.slice(0, 8)}
                      </p>
                      {(member as any).email && (member as any).email !== member.full_name && (
                        <p className="text-xs text-muted-foreground truncate">
                          {(member as any).email}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
          {selectedUserIds.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {selectedUserIds.length} usuário(s) selecionado(s)
            </p>
          )}
        </div>
      )}
    </div>
  );
}
